import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { BrandingProvider } from "./context/BrandingContext";
import { ThemeProvider } from "./context/ThemeContext";
import Layout from "./components/Layout";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import UsersPage from "./pages/UsersPage";
import ProjectsPage from "./pages/ProjectsPage";
import TeamsPage from "./pages/TeamsPage";
import WBSPage from "./pages/WBSPage";
import MilestonesTasksPage from "./pages/MilestonesTasksPage";
import DailyLogsPage from "./pages/DailyLogsPage";
import ManpowerPage from "./pages/ManpowerPage";
import MaterialsPage from "./pages/MaterialsPage";
import TeamReportsPage from "./pages/TeamReportsPage";
import ProductivityPage from "./pages/ProductivityPage";
import BudgetsPage from "./pages/BudgetsPage";
import CostsPage from "./pages/CostsPage";
import BudgetVsActualPage from "./pages/BudgetVsActualPage";
import InvoicesPage from "./pages/InvoicesPage";
import PaymentsPage from "./pages/PaymentsPage";
import VariationsPage from "./pages/VariationsPage";
import FinancialDashboardPage from "./pages/FinancialDashboardPage";
import DocumentsPage from "./pages/DocumentsPage";
import ApprovalsPage from "./pages/ApprovalsPage";
import AuditLogPage from "./pages/AuditLogPage";
import ReportsPage from "./pages/ReportsPage";
import ExecutiveDashboardPage from "./pages/ExecutiveDashboardPage";
import ProjectDashboardPage from "./pages/ProjectDashboardPage";
import SiteDashboardPage from "./pages/SiteDashboardPage";
import NotificationsPage from "./pages/NotificationsPage";
import CalendarPage from "./pages/CalendarPage";
import MeetingNotesPage from "./pages/MeetingNotesPage";
import SettingsPage from "./pages/SettingsPage";
import UserGuidePage from "./pages/UserGuidePage";
import TaxSettingsPage from "./pages/TaxSettingsPage";
import VATReportPage from "./pages/VATReportPage";
import LegalPage from "./pages/LegalPage";
import LegalAdminPage from "./pages/LegalAdminPage";
import SuperAdminPage from "./pages/SuperAdminPage";
import PayrollPage from "./pages/PayrollPage";
import InventoryPage from "./pages/InventoryPage";
import AccountingPage from "./pages/AccountingPage";
import AdvancedReportsPage from "./pages/AdvancedReportsPage";
import RolePermissionsPage from "./pages/RolePermissionsPage";
import InviteAcceptPage from "./pages/InviteAcceptPage";
import MarketingManualPage from "./pages/MarketingManualPage";
import DemoRequestPage from "./pages/DemoRequestPage";
import SubmittalsPage from "./pages/SubmittalsPage";
import PlanningPage from "./pages/PlanningPage";
import WebsiteHomePage from "./pages/website/HomePage";
import WebsiteFeaturesPage from "./pages/website/FeaturesPage";
import WebsiteModulesPage from "./pages/website/ModulesPage";
import WebsiteAboutPage from "./pages/website/AboutPage";

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F9F9] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#E0E0E0] border-t-[#002FA7] rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/website" replace />;
  }

  if (roles && !roles.includes(user.role) && user.role !== 'Super Admin') {
    return <Navigate to={user.role === 'Super Admin' ? '/super-admin' : '/'} replace />;
  }

  return <Layout>{children}</Layout>;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F9F9] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#E0E0E0] border-t-[#002FA7] rounded-full animate-spin" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <BrandingProvider>
        <ThemeProvider>
        <Routes>
          <Route path="/login" element={<PublicRoute><Navigate to="/demo?tab=signin" replace /></PublicRoute>} />
          <Route path="/" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/users" element={<ProtectedRoute roles={["Admin", "IT Admin", "General Manager", "Project Manager"]}><UsersPage /></ProtectedRoute>} />
          <Route path="/projects" element={<ProtectedRoute><ProjectsPage /></ProtectedRoute>} />
          <Route path="/teams" element={<ProtectedRoute><TeamsPage /></ProtectedRoute>} />
          <Route path="/wbs" element={<ProtectedRoute><WBSPage /></ProtectedRoute>} />
          <Route path="/milestones-tasks" element={<ProtectedRoute><MilestonesTasksPage /></ProtectedRoute>} />
          <Route path="/daily-logs" element={<ProtectedRoute><DailyLogsPage /></ProtectedRoute>} />
          <Route path="/manpower" element={<ProtectedRoute><ManpowerPage /></ProtectedRoute>} />
          <Route path="/materials" element={<ProtectedRoute><MaterialsPage /></ProtectedRoute>} />
          <Route path="/team-reports" element={<ProtectedRoute><TeamReportsPage /></ProtectedRoute>} />
          <Route path="/productivity" element={<ProtectedRoute><ProductivityPage /></ProtectedRoute>} />
          <Route path="/budgets" element={<ProtectedRoute><BudgetsPage /></ProtectedRoute>} />
          <Route path="/costs" element={<ProtectedRoute><CostsPage /></ProtectedRoute>} />
          <Route path="/budget-vs-actual" element={<ProtectedRoute><BudgetVsActualPage /></ProtectedRoute>} />
          <Route path="/invoices" element={<ProtectedRoute><InvoicesPage /></ProtectedRoute>} />
          <Route path="/payments" element={<ProtectedRoute><PaymentsPage /></ProtectedRoute>} />
          <Route path="/variations" element={<ProtectedRoute><VariationsPage /></ProtectedRoute>} />
          <Route path="/financial-dashboard" element={<ProtectedRoute><FinancialDashboardPage /></ProtectedRoute>} />
          <Route path="/documents" element={<ProtectedRoute><DocumentsPage /></ProtectedRoute>} />
          <Route path="/submittals" element={<ProtectedRoute><SubmittalsPage /></ProtectedRoute>} />
          <Route path="/project-planning" element={<ProtectedRoute><PlanningPage /></ProtectedRoute>} />
          <Route path="/approvals" element={<ProtectedRoute><ApprovalsPage /></ProtectedRoute>} />
          <Route path="/audit-log" element={<ProtectedRoute><AuditLogPage /></ProtectedRoute>} />
          <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
          <Route path="/executive-dashboard" element={<ProtectedRoute><ExecutiveDashboardPage /></ProtectedRoute>} />
          <Route path="/project-dashboard" element={<ProtectedRoute><ProjectDashboardPage /></ProtectedRoute>} />
          <Route path="/site-dashboard" element={<ProtectedRoute><SiteDashboardPage /></ProtectedRoute>} />
          <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
          <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
          <Route path="/meetings" element={<ProtectedRoute><MeetingNotesPage /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute roles={["Admin", "IT Admin"]}><SettingsPage /></ProtectedRoute>} />
          <Route path="/tax-settings" element={<ProtectedRoute roles={["IT Admin", "Project Manager"]}><TaxSettingsPage /></ProtectedRoute>} />
          <Route path="/vat-report" element={<ProtectedRoute><VATReportPage /></ProtectedRoute>} />
          <Route path="/payroll" element={<ProtectedRoute><PayrollPage /></ProtectedRoute>} />
          <Route path="/inventory" element={<ProtectedRoute><InventoryPage /></ProtectedRoute>} />
          <Route path="/accounting" element={<ProtectedRoute><AccountingPage /></ProtectedRoute>} />
          <Route path="/advanced-reports" element={<ProtectedRoute><AdvancedReportsPage /></ProtectedRoute>} />
          <Route path="/role-permissions" element={<ProtectedRoute><RolePermissionsPage /></ProtectedRoute>} />
          <Route path="/legal/:type" element={<ProtectedRoute><LegalPage /></ProtectedRoute>} />
          <Route path="/legal-admin" element={<ProtectedRoute roles={["Admin", "IT Admin"]}><LegalAdminPage /></ProtectedRoute>} />
          <Route path="/super-admin" element={<ProtectedRoute roles={["Super Admin"]}><SuperAdminPage /></ProtectedRoute>} />
          <Route path="/user-guide" element={<ProtectedRoute><UserGuidePage /></ProtectedRoute>} />
          <Route path="/marketing-manual" element={<ProtectedRoute><MarketingManualPage /></ProtectedRoute>} />
          <Route path="/invite/:token" element={<InviteAcceptPage />} />
          <Route path="/demo" element={<DemoRequestPage />} />
          <Route path="/website" element={<WebsiteHomePage />} />
          <Route path="/website/features" element={<WebsiteFeaturesPage />} />
          <Route path="/website/modules" element={<WebsiteModulesPage />} />
          <Route path="/website/about" element={<WebsiteAboutPage />} />
          <Route path="*" element={<Navigate to="/website" replace />} />
        </Routes>
        </ThemeProvider>
        </BrandingProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
